import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useReducer, useEffect } from 'react';

import { groupsService, Group, JoinGroupRequest, SuggestGroupRequest } from '@cobu/api-client';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import groupsReducer, {
  GroupsState,
  setLoading,
  setGroups,
  updateGroupMembership,
  setError,
  clearError,
  reset
} from '../../store/groupsSlice';
import { useSelector } from 'react-redux';
import { logger } from '../../utils/logger';
// import Logger from '@/services/Logger';

// Minimal web-safe AsyncStorage wrapper using localStorage
const AsyncStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) return;
      window.localStorage.setItem(key, value);
    } catch {
      // ignore write failures (e.g., storage quota, privacy mode)
    }
  },
};

// Initial state from the slice
const initialState: GroupsState = {
  groups: {},
  isLoading: false,
  error: null,
  lastFetch: null,
};

// Storage key for persisting groups data
const GROUPS_STORAGE_KEY = 'groups_data';

// Combined query keys from both implementations
export const GROUPS_KEYS = {
  // From HEAD branch (complex state management)
  all: ['groups'],
  list: ['groups', 'list'],
  userGroups: ['groups', 'user'],
  // From other branch (simple service-based)
  groups: (buildingUuid: string) => ['groups', buildingUuid],
};

/**
 * Complex hook for fetching groups with state management, persistence, and analytics
 * Use this when you need full state management, caching, and user interaction tracking
 */
export const useGroupsWithState = () => {
  const [state, dispatch] = useReducer(groupsReducer, initialState);
  const { user } = useSelector((state: any) => state.auth);

  const queryClient = useQueryClient();

  // Load persisted data on mount
  useEffect(() => {
    const loadPersistedData = async () => {
      try {
        const stored = await AsyncStorage.getItem(GROUPS_STORAGE_KEY);
        if (stored) {
          const parsedData = JSON.parse(stored);
          if (parsedData.groups && Array.isArray(parsedData.groups)) {
            dispatch(setGroups(parsedData.groups));
          }
        }
      } catch (error) {
        logger.error('Failed to load persisted groups data:', error);
      }
    };

    loadPersistedData();
  }, []);

  // Persist groups data when it changes
  useEffect(() => {
    const persistData = async () => {
      try {
        const groupsArray = Object.values(state.groups);
        if (groupsArray.length > 0) {
          await AsyncStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify({ groups: groupsArray }));
        }
      } catch (error) {
        logger.error('Failed to persist groups data:', error);
      }
    };

    if (Object.keys(state.groups).length > 0) {
      persistData();
    }
  }, [state.groups]);

  const queryResult = useQuery({
    queryKey: GROUPS_KEYS.list,
    queryFn: async () => {
      dispatch(setLoading(true));
      try {
        const response = await groupsService.getGroups(user.building.uuid);
        dispatch(setGroups(response));
        return response;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch groups';
        dispatch(setError(errorMessage));
        throw error;
      }
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Only refetch if data is older than 5 minutes
    enabled: !state.lastFetch || (Date.now() - state.lastFetch > 5 * 60 * 1000),
  });

  // Ensure local reducer state is synchronized even when React Query serves cached data
  useEffect(() => {
    if (Array.isArray(queryResult.data) && queryResult.data.length >= 0) {
      dispatch(setGroups(queryResult.data));
    }
  }, [queryResult.data, dispatch]);

  return {
    groups: Object.values(state.groups),
    groupsMap: state.groups,
    isLoading: state.isLoading || queryResult.isLoading,
    error: state.error || queryResult.error?.message,
    refetch: queryResult.refetch,
    dispatch, // Expose dispatch for mutations
  };
};

/**
 * Simple hook for fetching groups via service layer
 * Use this for lightweight data fetching without state management overhead
 */
export const useGetGroupsQuery = (buildingUuid: string) => {
  return useQuery({
    queryKey: GROUPS_KEYS.groups(buildingUuid),
    queryFn: () => groupsService.getGroups(buildingUuid),
    enabled: !!buildingUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Simple hook for fetching groups via service layer
 * Use this for lightweight data fetching without state management overhead
 */
export const useGetGroupsOfUserQuery = () => {
  return useQuery({
    queryKey: GROUPS_KEYS.userGroups,
    queryFn: () => groupsService.getGroupsOfUser(),
    staleTime: 0,
  });
};

/**
 * Hook for joining a group
 */
export const useJoinGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: JoinGroupRequest) => groupsService.joinGroup(request),
    onSuccess: (_, variables) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: GROUPS_KEYS.all });

      // Track analytics
      /* ANALYTICS_DISABLED: JOIN_GROUP ('join_group')
        analyticsService.trackEvent(AnalyticsEvent.JOIN_GROUP, {
                groupId: variables.groupId,
              });
        */
    },
    onError: (error) => {
      logger.error('Join group error:', error);
    },
  });
};

/**
 * Hook for leaving a group
 */
export const useLeaveGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => groupsService.leaveGroup(groupId),
    onSuccess: (_, groupId) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: GROUPS_KEYS.all });

      // Track analytics
      /* ANALYTICS_DISABLED: LEAVE_GROUP ('leave_group')
        analyticsService.trackEvent(AnalyticsEvent.LEAVE_GROUP, {
                groupId,
              });
        */
    },
    onError: (error) => {
      logger.error('Leave group error:', error);
    },
  });
};

/**
 * Hook for suggesting a new group
 */
export const useSuggestGroupMutation = () => {
  return useMutation({
    mutationFn: (request: SuggestGroupRequest) => groupsService.suggestGroup(request),
    onSuccess: (_, variables) => {
      // Logger.log('Suggest group success:', variables);
    },
    onError: (error) => {
      logger.error('Suggest group error:', error);
    },
  });
};

/**
 * Hook for getting groups organized by membership
 * Uses the complex state management hook under the hood
 */
export const useOrganizedGroups = () => {
  const { groups, isLoading, error, dispatch, refetch } = useGroupsWithState();

  const organizedGroups = groups.reduce<{
    myGroups: Group[];
    otherGroups: Group[];
  }>(
    (acc, group) => {
      if (group.meta.userIsMember) {
        acc.myGroups.push(group);
      } else {
        acc.otherGroups.push(group);
      }
      return acc;
    },
    { myGroups: [], otherGroups: [] }
  );

  const clearErrors = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    myGroups: organizedGroups.myGroups,
    otherGroups: organizedGroups.otherGroups,
    allGroups: groups,
    isLoading,
    error,
    clearErrors,
    refetch,
  };
}; 
